import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';
import "./HomeScreen.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBalanceScale, faExchangeAlt, faCertificate, faHandHoldingUsd, faRoute, faHeadphones, faLightbulb, faFan, faCompass, faKeyboard, faSnowflake, faSun, faChair, faMobile, faSquareCheck, faFileContract, faIdBadge, faBan, faMapMarkedAlt, faReceipt, faShieldAlt, faMotorcycle, faCarSide, faIdCard, faSearch, faTags } from "@fortawesome/free-solid-svg-icons";
import ServiceCard from "../components/ServiceCard";
import LoanCard from "../components/LoanCard";
import BikeCard from "../components/BikeCard";
import FAQSection from "../components/FAQSection";
import { isAuthenticated, getToken } from "../components/AuthUtils";
import ExpiryNotificationBanner, { FloatingExpiryBadge } from "../components/ExpiryNotificationBanner";
import AdSense from "../components/AdSense";
import checkChallan from "../assets/check-challan.png";
import rtoServices from "../assets/rto-services.png";
import bikeInsurance from "../assets/bike-insurance.png";
import carInsurance from "../assets/car-insurance.png";
import sellCar from "../assets/sell-car.png";
import dlTest from "../assets/dl-test.png";
import vehicleSearch from "../assets/search-vehicle.png";
import carServiceReport from "../assets/car-service-report.png";
import drivingLicense from "../assets/driving-license.png";
import ownershipTransfer from "../assets/ownership-transfer.png";
import Ind from "../assets/Ind.png";
// Loan icons
import vehicleIcon from "../assets/vehicleLoan.png";
import instantIcon from "../assets/instantLoan.png";
import personalIcon from "../assets/personalLoan.png";
import refinanceIcon from "../assets/refinanceLoan.png";
// Bike icons
import sellBike from "../assets/sell-bike.png";
import buyNewBike from "../assets/buy-bike.png";
import buyUsedBike from "../assets/buy-used-bike.png";
import cashOnBike from "../assets/cash-on-bike.png";
import NearbyPlacesCard from "../components/NearbyPlacesCard";
import { FaShieldAlt, FaUserCheck, FaClock, FaHeadset, FaStar, FaUsers, FaAward } from "react-icons/fa";
// Nearby Places icons
import petrolPump from "../assets/petrol-pump.png";
import policeStation from "../assets/police-station.png";
import chargingStation from "../assets/charging-station.png";
import mechanic from "../assets/mechanic.png";

// Add these two image imports for the clickable images
import leftImage from "../assets/announcement2.png"; // Replace with your actual image path
import rightImage from "../assets/announcement1.png"; // Replace with your actual image path

function HomeScreen() {
  const navigate = useNavigate();
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [error, setError] = useState("");

  // Expiry notification state
  const [expiryNotifications, setExpiryNotifications] = useState([]);
  const [showExpiryModal, setShowExpiryModal] = useState(false);

  // Fetch expiry notifications on mount if authenticated
  useEffect(() => {
    const fetchExpiryNotifications = async () => {
      if (!isAuthenticated()) return;

      try {
        const token = getToken();
        const response = await fetch('https://api.rtoxpert.com/api/notifications/expiry/check', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.length > 0) {
            setExpiryNotifications(data.data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch expiry notifications:', err);
      }
    };

    fetchExpiryNotifications();
  }, []);

  // Handle dismiss notification
  const handleDismissNotification = async (notificationId) => {
    try {
      const token = getToken();
      const response = await fetch(`https://api.rtoxpert.com/api/notifications/${notificationId}/dismiss`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setExpiryNotifications(prev =>
          prev.filter(n => n.notification_id !== notificationId)
        );
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  // Handle update document - navigate to garage/vehicle page
  const handleUpdateDocument = (notification) => {
    setShowExpiryModal(false);
    // Navigate to profile/garage where user can update documents
    navigate('/profile');
  };

  // Indian vehicle number validation function
  const validateVehicleNumber = (number) => {
    // Remove spaces and convert to uppercase for consistency
    const cleanedNumber = number.replace(/\s/g, "").toUpperCase();

    // Comprehensive regex for Indian vehicle numbers
    const regex = /^(?:[A-Z]{2}\d{1,2}(?:[A-Z]{1,2}|\d{1,2}|[A-Z]{2}\d{1,2})?\d{1,4}|[A-Z]{2}\d{2}[A-Z]{1,2}\d{1,4})$/;

    // Valid state codes
    const validStateCodes = [
      "AN", "AP", "AR", "AS", "BR", "CH", "CG", "DD", "DL", "GA", "GJ", "HR", "HP", "JH", "JK", "KA", "KL",
      "LD", "MP", "MH", "MN", "ML", "MZ", "NL", "OD", "PY", "PB", "RJ", "SK", "TN", "TS", "TR", "UP", "UK", "WB"
    ];

    // Check if the number matches the regex and starts with a valid state code
    if (!regex.test(cleanedNumber)) {
      return "Invalid vaahan number format. Please enter a valid Indian vaahan number (e.g., DL12AA1234, MH04TC1234).";
    }

    // Extract first two letters and check if they are a valid state code
    const stateCode = cleanedNumber.substring(0, 2);
    if (!validStateCodes.includes(stateCode)) {
      return "Invalid state code. Please use a valid Indian state code (e.g., DL, MH, KA).";
    }

    return "";
  };

  const handleSearch = () => {
    const validationError = validateVehicleNumber(vehicleNumber);
    if (validationError) {
      setError(validationError);
    } else {
      setError("");
      navigate('/searchDetails', { state: { vehicleNumber: vehicleNumber.replace(/\s/g, "").toUpperCase() } });
    }
  };

  // Functions to handle image clicks
  const handleLeftImageClick = () => {
    navigate('/AdhaarVerification'); // Replace with your desired route
  };

  const handleRightImageClick = () => {
    navigate('/DLVerification'); // Replace with your desired route
  };

  // Services that don't require authentication
  const publicServices = ['/searchDetails'];

  const services = [
    { id: 1, title: "Check Challans", icon: <FontAwesomeIcon icon={faReceipt} style={{ color: "var(--color-primary)" }} />, path: "/checkChallan" },
    { id: 3, title: "Car Insurance", icon: <FontAwesomeIcon icon={faShieldAlt} style={{ color: "var(--color-primary)" }} />, path: "/carInsurance" },
    { id: 4, title: "Bike Insurance", icon: <FontAwesomeIcon icon={faMotorcycle} style={{ color: "var(--color-primary)" }} />, path: "/bikeInsurance" },
    { id: 5, title: "Sell Your Car", icon: <FontAwesomeIcon icon={faCarSide} style={{ color: "var(--color-primary)" }} />, path: "/sellYourCar", hidden: true },
    { id: 6, title: "DL Test", icon: <FontAwesomeIcon icon={faIdCard} style={{ color: "var(--color-primary)" }} />, path: "/DLTest" },
    { id: 7, title: "Vaahan Search", icon: <FontAwesomeIcon icon={faSearch} style={{ color: "var(--color-primary)" }} />, path: "/searchDetails" },
    { id: 9, title: "Driving License", icon: <FontAwesomeIcon icon={faIdCard} style={{ color: "var(--color-primary)" }} />, path: "/drivingLicenseRenewal" },
    { id: 10, title: "Ownership Transfer", icon: <FontAwesomeIcon icon={faExchangeAlt} style={{ color: "var(--color-primary)" }} />, path: "/ownershipTransfer" },
    { id: 11, title: "Duplicate RC", icon: <FontAwesomeIcon icon={faCertificate} style={{ color: "var(--color-primary)" }} />, path: "/duplicateRegistrationCertificate" },
    { id: 12, title: "Bank NOC & Form 35", icon: <FontAwesomeIcon icon={faFileContract} style={{ color: "var(--color-primary)" }} />, path: "/bankNoc" },
    { id: 13, title: "HSRP Plate", icon: <FontAwesomeIcon icon={faIdBadge} style={{ color: "var(--color-primary)" }} />, path: "/highSecurityPlate" },
    { id: 14, title: "Hypothecation Termination", icon: <FontAwesomeIcon icon={faBan} style={{ color: "var(--color-primary)" }} />, path: "/hypothecationTermination" },
    { id: 15, title: "Interstate RTO NOC", icon: <FontAwesomeIcon icon={faMapMarkedAlt} style={{ color: "var(--color-primary)" }} />, path: "/rtoNOC" },
  ];

  const loans = [
    { id: 1, title: "Vaahan Loan", image: vehicleIcon, path: "/loan" },
    { id: 2, title: "Instant Loan", image: instantIcon, path: "/loan" },
    { id: 3, title: "Personal Loan", image: personalIcon, path: "/loan" },
    { id: 4, title: "Refinance Loan", image: refinanceIcon, path: "/loan" },
  ];

  const carServices = [
    { id: 1, title: "Sell Your Car", icon: <FontAwesomeIcon icon={faCarSide} style={{ color: "var(--color-primary)" }} />, path: "/sellYourCar", hidden: true },
    { id: 2, title: "Buy New Car", icon: <FontAwesomeIcon icon={faCarSide} style={{ color: "var(--color-primary)" }} />, url: "https://www.cardekho.com", hidden: true },
    { id: 3, title: "Buy Used Car", icon: <FontAwesomeIcon icon={faSearch} style={{ color: "var(--color-primary)" }} />, url: "https://www.spinny.com", hidden: true },
    { id: 4, title: "Cash on Car", icon: <FontAwesomeIcon icon={faHandHoldingUsd} style={{ color: "var(--color-primary)" }} />, path: "/cashOnCar", hidden: true },
    {
      id: 5,
      title: "FasTag",
      icon: <FontAwesomeIcon icon={faTags} style={{ color: "var(--color-primary)" }} />,
      url: "https://www.npci.org.in/what-we-do/netc-fastag/request-for-netc-fastag",
      hidden: true
    },
    {
      id: 6,
      title: "Roadside Assistant",
      icon: <FontAwesomeIcon icon={faMotorcycle} style={{ color: "var(--color-primary)" }} />,
      url: "https://www.allianzassistance.in/4-wheeler-roadside-assistance.html",
      hidden: true
    },
    { id: 7, title: "Renew Insurance", icon: <FontAwesomeIcon icon={faShieldAlt} style={{ color: "var(--color-primary)" }} />, path: "/carInsurance" },
    { id: 8, title: "Vaahan Valuation", icon: <FontAwesomeIcon icon={faBalanceScale} style={{ color: "var(--color-primary)" }} />, url: "https://www.cars24.com/sell-used-car/" },
    {
      id: 9,
      title: "Transfer Ownership",
      path: "/ownershipTransfer",
      icon: <FontAwesomeIcon icon={faExchangeAlt} style={{ color: "var(--color-primary)" }} />,
    },
    {
      id: 10, title: "PUC Certificate",
      path: "/pucCertificate",
      icon: <FontAwesomeIcon icon={faCertificate} style={{ color: "var(--color-primary)" }} />
    },
    {
      id: 11,
      title: "Car Loan",
      path: "/loan",
      icon: <FontAwesomeIcon icon={faHandHoldingUsd} style={{ color: "var(--color-primary)" }} />,
    },
  ];

  const bikes = [
    { id: 1, title: "Sell Your Bike", image: sellBike, url: "https://www.olx.in/bikes_c1413" },
    { id: 2, title: "Buy New Bike", image: buyNewBike, url: "https://www.bikewale.com" },
    { id: 3, title: "Buy Used Bike", image: buyUsedBike, url: "https://droom.in" },
    { id: 4, title: "Cash On Bike", image: cashOnBike, path: "/cashOnBike" },
  ];

  const accessories = [
    { id: 1, title: "Audio Systems", icon: <FontAwesomeIcon icon={faHeadphones} style={{ color: "var(--color-primary)" }} />, searchTerm: "car audio system" },
    { id: 2, title: "Car Lights", icon: <FontAwesomeIcon icon={faLightbulb} style={{ color: "var(--color-primary)" }} />, searchTerm: "car lights" },
    { id: 3, title: "Coolers & Fans", icon: <FontAwesomeIcon icon={faFan} style={{ color: "var(--color-primary)" }} />, searchTerm: "car cooler fan" },
    { id: 4, title: "Navigation", icon: <FontAwesomeIcon icon={faCompass} style={{ color: "var(--color-primary)" }} />, searchTerm: "car navigation system" },
    { id: 5, title: "Interior Gadgets", icon: <FontAwesomeIcon icon={faKeyboard} style={{ color: "var(--color-primary)" }} />, searchTerm: "car interior gadgets" },
    { id: 6, title: "AC Accessories", icon: <FontAwesomeIcon icon={faSnowflake} style={{ color: "var(--color-primary)" }} />, searchTerm: "car ac accessories" },
    { id: 7, title: "Sun Shades", icon: <FontAwesomeIcon icon={faSun} style={{ color: "var(--color-primary)" }} />, searchTerm: "car sun shade" },
    { id: 8, title: "Seat Covers", icon: <FontAwesomeIcon icon={faChair} style={{ color: "var(--color-primary)" }} />, searchTerm: "car seat cover" },
    { id: 9, title: "Mobile Holders", icon: <FontAwesomeIcon icon={faMobile} style={{ color: "var(--color-primary)" }} />, searchTerm: "car mobile holder" },
    { id: 10, title: "Verified Accessories", icon: <FontAwesomeIcon icon={faSquareCheck} style={{ color: "var(--color-primary)" }} />, searchTerm: "car accessories" },
  ];

  const nearByPlaces = [
    { id: 1, title: "Petrol Pump", image: petrolPump },
    { id: 2, title: "Police Station", image: policeStation },
    { id: 3, title: "Charging Station", image: chargingStation },
    { id: 4, title: "Mechanic", image: mechanic },
  ];

  const handleServiceClick = (path, url) => {
    if (url) {
      window.open(url, '_blank');
      return;
    }

    if (path) {
      // Check if the service requires authentication
      if (!publicServices.includes(path) && !isAuthenticated()) {
        // Redirect to phone screen for authentication
        navigate('/phone');
        return;
      }

      // Navigate to the service
      navigate(path);
    }
  };

  const handleLoanClick = (path) => {
    if (!isAuthenticated()) {
      navigate('/phone');
      return;
    }
    navigate(path);
  };

  const handleBikeServiceClick = (path, url) => {
    if (url) {
      window.open(url, '_blank');
      return;
    }

    if (path) {
      if (!isAuthenticated()) {
        navigate('/phone');
        return;
      }
      navigate(path);
    }
  };

  return (
    <div className="home-screen">
      <Helmet>
        <title>VAAHANBUDDY - Vaahan Services Assistant for India</title>
        <meta name="description" content="Get all your vaahan-related tasks done easily with VAAHANBUDDY." />
        <meta name="keywords" content="RTO services, vaahan services, India, check challan, car insurance, bike insurance, ownership transfer, driving license, NOC" />
        <meta property="og:title" content="Home | VAAHANBUDDY - Vaahan Services Assistant for India" />
        <meta property="og:description" content="Manage your vaahan services with VAAHANBUDDY, including checking challans, RTO assistance, insurance, and more." />
        <meta property="og:image" content="https://rtoxpert.com/assets/vehicle-search.png" />
        <meta property="og:url" content="https://rtoxpert.com" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Document Expiry Notification Badge */}
      <FloatingExpiryBadge
        count={expiryNotifications.length}
        onClick={() => setShowExpiryModal(true)}
      />

      {/* Document Expiry Notification Modal */}
      {showExpiryModal && (
        <ExpiryNotificationBanner
          notifications={expiryNotifications}
          onDismiss={handleDismissNotification}
          onUpdate={handleUpdateDocument}
          onClose={() => setShowExpiryModal(false)}
        />
      )}
      <div className="vehicle-box">
        <div className="vehicle-search-section">
          <div className="vehicles-image">
            {/* This div uses background-image in CSS to show SUV + motorcycle */}
          </div>
          <div className="search-container">
            <h2>
              Check Your <span className="highlight">Vaahan</span> Details
            </h2>
            <div className="search-input-group">
              <div className="input-prefix">
                <img src={Ind} alt="IND" />
              </div>
              <input
                type="text"
                placeholder="e.g., DL12AA1234, MH04TC1234"
                value={vehicleNumber}
                maxLength={11}
                onChange={(e) => {
                  setVehicleNumber(e.target.value.toUpperCase());
                  setError(""); // Clear error on input change
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <button
                className="search-btn"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
            {error && <p className="error-message" style={{ color: "red", marginTop: "10px" }}>{error}</p>}
          </div>
        </div>
      </div>

      {/* Ad Banner - Below Hero Section */}
      <AdSense adSlot="1234567890" adFormat="horizontal" className="home-ad-banner" />

      {/* Clickable Images Section */}
      {/* <div className="clickable-images-section">
        <div className="image-container">
          <img 
            src={leftImage} 
            alt="Left Option" 
            className="clickable-image left-image"
            onClick={handleLeftImageClick}
          />
          <img 
            src={rightImage} 
            alt="Right Option" 
            className="clickable-image right-image"
            onClick={handleRightImageClick}
          />
        </div>
      </div> */}

      {/* Services Section */}
      <div className="services-section">
        <div className="accessories-cards-container">
          {services.filter(s => !s.hidden).map((service) => (
            <ServiceCard
              key={service.id}
              title={service.title}
              icon={service.icon}
              onClick={() => handleServiceClick(service.path)}
            />
          ))}
        </div>
      </div>

      {/* Loans Section */}
      {/* 
      <div className="loan-section">
        <h2 className="loan-header">Loans</h2>
        <div className="loan-scroll">
          {loans.map((loan) => (
            <LoanCard
              key={loan.id}
              title={loan.title}
              image={loan.image}
              onClick={() => handleLoanClick(loan.path)}
            />
          ))}
        </div>
      </div>
      */}

      {/* Ad Banner - Between Loans and Cars */}
      <AdSense adSlot="0987654321" adFormat="auto" className="home-ad-between-sections" />

      {/* Cars Section */}
      <div className="cars-section">
        <h2>Cars</h2>
        <div className="accessories-cards-container">
          {carServices.filter(s => !s.hidden).map((service) => (
            <ServiceCard
              key={service.id}
              title={service.title}
              icon={service.icon}
              isSmall
              onClick={() => handleServiceClick(service.path, service.url)}
            />
          ))}
        </div>
      </div>

      {/* Bike Section */}
      {/* 
      <div className="bike-section">
        <h2 className="bike-header">Bikes</h2>
        <div className="bike-scroll">
          {bikes.map((service) => (
            <BikeCard
              key={service.id}
              title={service.title}
              image={service.image}
              onClick={() => handleBikeServiceClick(service.path, service.url)}
            />
          ))}
        </div>
      </div>
      */}
      {/* Accessories Section */}
      <div className="accessories-section">
        <h2>Accessories</h2>
        <div className="accessories-cards-container">
          {accessories.map((accessory) => (
            <div
              key={accessory.id}
              className="accessory-card"
              onClick={() => {
                const searchQuery = encodeURIComponent(accessory.searchTerm || accessory.title);
                const flipkartUrl = `https://www.flipkart.com/search?q=${searchQuery}`;
                window.open(flipkartUrl, '_blank');
              }}
            >
              <div className="accessory-icon">{accessory.icon}</div>
              <div className="accessory-title">{accessory.title}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Nearby Places Section */}
      <div className="nearByPlaces-section">
        <h2 className="nearByPlaces-header">Nearby Places</h2>
        <div className="nearByPlaces-scroll">
          {nearByPlaces.map((place, index) => (
            <NearbyPlacesCard key={index} title={place.title} image={place.image} />
          ))}
        </div>
      </div>

      {/* Ad Banner - Before FAQ */}
      <AdSense adSlot="1122334455" adFormat="horizontal" className="home-ad-before-faq" />

      <FAQSection />

      {/* Welcome Section - Rich Text Content for SEO */}
      <section className="welcome-section">
        <div className="welcome-content">
          <h2>Welcome to VaahanBuddy</h2>
          <p>
            VaahanBuddy is India's leading vehicle services platform, designed to simplify all your
            vehicle-related tasks. From checking traffic challans and renewing insurance to handling
            complex RTO documentation like ownership transfers and NOC applications, we provide
            comprehensive assistance through our network of experienced agents across all 29 states
            and 8 union territories of India.
          </p>
          <p>
            Our platform combines cutting-edge technology with personalized human support, ensuring
            that every vehicle owner can manage their documentation efficiently without the hassle
            of visiting multiple offices or dealing with complex paperwork. Whether you own a car,
            motorcycle, or commercial vehicle, VaahanBuddy is your trusted companion for all
            vehicle-related services.
          </p>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="trust-section">
        <h2>Trusted by Thousands of Vehicle Owners</h2>
        <div className="trust-stats">
          <div className="trust-stat">
            <FaUsers className="trust-icon" />
            <span className="trust-number">50,000+</span>
            <span className="trust-label">Happy Customers</span>
          </div>
          <div className="trust-stat">
            <FaStar className="trust-icon" />
            <span className="trust-number">4.8/5</span>
            <span className="trust-label">Customer Rating</span>
          </div>
          <div className="trust-stat">
            <FaAward className="trust-icon" />
            <span className="trust-number">15+</span>
            <span className="trust-label">Services Offered</span>
          </div>
          <div className="trust-stat">
            <FaHeadset className="trust-icon" />
            <span className="trust-number">100+</span>
            <span className="trust-label">Expert Agents</span>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <h2>Why Choose VaahanBuddy?</h2>
        <div className="why-choose-grid">
          <div className="why-choose-card">
            <FaShieldAlt className="why-icon" />
            <h3>Secure & Trusted</h3>
            <p>
              Your personal and vehicle information is protected with industry-standard encryption.
              We follow strict data privacy protocols and never share your information without consent.
            </p>
          </div>
          <div className="why-choose-card">
            <FaUserCheck className="why-icon" />
            <h3>Expert Assistance</h3>
            <p>
              Our team of experienced agents understands the intricacies of RTO processes across
              different states. They guide you through every step, ensuring accurate documentation.
            </p>
          </div>
          <div className="why-choose-card">
            <FaClock className="why-icon" />
            <h3>Save Time</h3>
            <p>
              No more waiting in long queues at RTO offices. Submit your requests online and let
              our agents handle the legwork while you focus on what matters most.
            </p>
          </div>
          <div className="why-choose-card">
            <FaHeadset className="why-icon" />
            <h3>24/7 Support</h3>
            <p>
              Have questions? Our customer support team is available round the clock to assist you
              with queries, track your requests, and provide timely updates on your services.
            </p>
          </div>
        </div>
      </section>

      {/* About Platform Section */}
      <section className="about-platform-section">
        <div className="about-platform-content">
          <h2>About VaahanBuddy Platform</h2>
          <div className="about-text">
            <p>
              VaahanBuddy was created with a simple mission: to make vehicle ownership hassle-free
              for every Indian. We understand that dealing with RTO offices, insurance companies,
              and various documentation can be time-consuming and confusing. That's why we've built
              a comprehensive platform that brings all these services to your fingertips.
            </p>
            <p>
              Our services cover the complete lifecycle of vehicle ownership. Starting from purchasing
              a vehicle, we help you with registration verification, insurance procurement, challan
              checking, and all necessary RTO documentation. When it's time to sell or transfer
              ownership, our agents ensure smooth transitions with proper paperwork and compliance.
            </p>
            <p>
              We're proud to be a product of <strong>Raisclab Advisory LLP</strong>, committed to
              innovation and customer satisfaction. Our team constantly works to improve the platform,
              adding new features and services based on user feedback and changing regulatory requirements.
            </p>
          </div>
          <div className="about-cta">
            <a href="/about" className="learn-more-btn">Learn More About Us</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomeScreen;